import { Typography } from "@mui/material";
import { get } from "lodash";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";

import * as S from "../../constants/StringConstants";
import { IAffiliate } from "../../store/models/affiliate.model";
import { IRootState } from "../../store/reducers";
import { fetchAffiliatesList, removeAffiliate } from "../../store/reducers/affiliates";

import AppBar from "src/components/AppBar/AppBar";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import GuardedComponent from "src/components/GuardedComponent/GuardedComponent";
import Icon, { IconName } from "src/components/Icon/Icon";
import { UserRole } from "src/store/models/user.model";
import { hasAnyAuthority } from "src/util/roleAuthorization";
import { openUrl } from "src/util/util.navigation";
import { renderAddress, renderPrimaryContact } from "../../components/Table/columnRenders";
import FilterContainer from "../../components/TableV2/FilterContainer";
import SearchFieldTool from "../../components/TableV2/SearchField";
import DisTable from "../../components/TableV2/Table";
import Title from "../../components/TableV2/Title";

interface IAffiliatesProps extends StateProps, DispatchProps, RouteComponentProps {}

interface IAffiliatesState {
  deletedId: number | null;
}

class Affiliates extends React.Component<IAffiliatesProps, IAffiliatesState> {
  constructor(props: IAffiliatesProps) {
    super(props);
    this.state = { deletedId: null };
    this.handleDeleteAffiliateConfirm = this.handleDeleteAffiliateConfirm.bind(this);
    this.handleDeleteAffiliateCancel = this.handleDeleteAffiliateCancel.bind(this);
  }

  handleDeleteAffiliatePopup = (affiliateID: number) => {
    this.setState({ deletedId: affiliateID });
  };

  async handleDeleteAffiliateConfirm() {
    const { deletedId } = this.state;
    if (deletedId !== null) {
      await this.props.removeAffiliate(deletedId);
      await this.props.fetchAffiliatesList();
      this.setState({ deletedId: null });
    }
  }

  handleDeleteAffiliateCancel() {
    this.setState({ deletedId: null });
  }

  render() {
    const {
      auth,
      fetchAffiliatesList,
      affiliates: { map, projection, total, listOptions, loading },
    } = this.props;

    const isAdmin = hasAnyAuthority(auth.user, [UserRole.Admin]);

    const columns = [
      { key: "name", title: S.AFF_COL_NAME, style: { minWidth: "12rem" } },
      {
        key: "address",
        title: S.AFF_COL_ADDRESS,
        render: item => renderAddress(item.address),
        style: { minWidth: "12rem" },
      },
      { key: "branch", title: S.AFF_COL_BRANCH, style: { minWidth: "12rem" } },
      { key: "phoneNumber", title: S.AFF_COL_PHONE, style: { minWidth: "12rem" } },
      {
        key: "primaryContact",
        title: S.AFF_COL_PRIMARY_CONTACT,
        render: item => renderPrimaryContact(item.primaryContact || (item.contacts && item.contacts[0])),
        style: { minWidth: "12rem" },
      },
      { key: "affiliateID", title: S.AFF_COL_AFFILIATE_ID, style: { minWidth: "12rem" } },
      { key: "externalID", title: S.AFF_COL_EXTERNAL_ID, style: { minWidth: "12rem" } },
      ...(isAdmin
        ? [
            {
              key: "delete",
              title: S.AFF_DELETE,
              style: { minWidth: "8rem" },
              render: (item: IAffiliate) => (
                <Icon
                  name={IconName.DeleteFileBlue}
                  size="16px"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.handleDeleteAffiliatePopup(item.affiliateID);
                  }}
                />
              ),
            },
          ]
        : []),
    ];

    return (
      <div className="table-view-v2__content">
        <div className="table-view__header-buttons-container">
          <AppBar icon={IconName.Affiliates} title={S.AFF_PAGE_TITLE} loading={loading}>
            <div className="table-view__buttons-container">
              <GuardedComponent
                authorizedRoles={[
                  UserRole.Admin,
                  UserRole.ProposalSpecialist,
                  UserRole.BranchManager,
                  UserRole.UnderwritingAssociate,
                ]}>
                <button className="side-nav__add-button" onClick={() => openUrl("/affiliates/new")}>
                  <Icon name={IconName.Plus} />

                  {S.NEW_AFFILIATE}
                </button>
              </GuardedComponent>
            </div>
          </AppBar>
        </div>

        <DisTable<IAffiliate>
          columns={columns}
          onRowClick={(data: IAffiliate) => {
            this.props.history.push(`/affiliates/${data.affiliateID}`);
          }}
          initialState={{ rows: 25, map, projection, total, ...listOptions }}
          onUpdate={async ({ page, rows, sort, query }) => {
            const result: any = await fetchAffiliatesList({ page, rows, sort, query });
            return {
              total: get(result, "value.data.totalRows") as number,
              data: get(result, "value.data.items") as IAffiliate[],
            };
          }}>
          <FilterContainer
            left={<Title text={S.AFF_TABLE_TITLE} />}
            right={
              <>
                <SearchFieldTool />
              </>
            }></FilterContainer>
        </DisTable>

        {this.state.deletedId !== null && (
          <PromptModal
            open
            title={S.AFF_CONFIRM}
            confirmText={S.AFF_DELETE}
            rejectText={S.AFF_CANCEL}
            onConfirm={this.handleDeleteAffiliateConfirm}
            onReject={this.handleDeleteAffiliateCancel}>
            <Typography variant="h6">{S.AFF_CONFIRM_DELETE}</Typography>
          </PromptModal>
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ affiliates, auth }: IRootState) => ({ affiliates, auth });
const mapDispatchToProps = { fetchAffiliatesList, removeAffiliate };

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(Affiliates);
